package ai.wagemix.net.models

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/*
 * Account creation, email verification and password reset go through the Wagemix API (accounts-and-email plan
 * §2.2). These are the wire shapes of those routes, and the field rules the site checks as the user types and the
 * API enforces.
 */

/** The field rules both sides apply, and the codes the API puts in an error's `value` when one is broken. */
object WMAccountRules {
    const val MIN_PASSWORD_LENGTH = 8
    /** The longest password (Ryan, 2026-11-01): past any passphrase or password-manager default. */
    const val MAX_PASSWORD_LENGTH = 128
    /** The longest first or last name, after trimming; `firestore.rules` holds a profile edit to the same. */
    const val MAX_NAME_LENGTH = 100
    val EMAIL_PATTERN = Regex("^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$")

    /** The field is empty, too long or malformed (an email, a name, a token the API never issued). */
    const val ERROR_INVALID = "invalid"
    /** The password breaks a strength rule. */
    const val ERROR_WEAK = "weak"
    /** The link's token is past its lifetime. */
    const val ERROR_EXPIRED = "expired"
    /** The link's token was already used, or a newer one replaced it. */
    const val ERROR_USED = "used"

    fun isValidEmail(email: String): Boolean = EMAIL_PATTERN.matches(email.trim())

    fun isStrongPassword(password: String): Boolean {
        return password.length >= MIN_PASSWORD_LENGTH &&
            password.any { it.isDigit() } &&
            password.any { it.isUpperCase() } &&
            password.any { it.isLowerCase() }
    }
}

/** The body of `POST /api/v1/accounts`. */
interface WMAccountCreate {
    val firstName: String
    val lastName: String
    val email: String
    val password: String

    companion object
}

fun WMAccountCreate.Companion.kxs(
    firstName: String,
    lastName: String,
    email: String,
    password: String,
): KxsWMAccountCreate {
    return KxsWMAccountCreate(firstName = firstName, lastName = lastName, email = email, password = password)
}

fun WMAccountCreate.toKxs(): KxsWMAccountCreate = when (this) {
    is KxsWMAccountCreate -> this
    else -> WMAccountCreate.kxs(firstName = firstName, lastName = lastName, email = email, password = password)
}

@Serializable
@SerialName("WMAccountCreate")
@ConsistentCopyVisibility
data class KxsWMAccountCreate internal constructor(
    @SerialName("first_name") override val firstName: String,
    @SerialName("last_name") override val lastName: String,
    override val email: String,
    override val password: String
): WMAccountCreate {
    // The password never reaches a log line through toString.
    override fun toString(): String = "KxsWMAccountCreate(firstName=$firstName, lastName=$lastName, email=$email)"
}

/** The answer to `POST /api/v1/accounts`: the new account's Firebase uid. */
interface WMAccountCreated {
    val uid: String

    companion object
}

fun WMAccountCreated.Companion.kxs(uid: String): KxsWMAccountCreated = KxsWMAccountCreated(uid = uid)

fun WMAccountCreated.toKxs(): KxsWMAccountCreated = when (this) {
    is KxsWMAccountCreated -> this
    else -> WMAccountCreated.kxs(uid = uid)
}

@Serializable
@SerialName("WMAccountCreated")
@ConsistentCopyVisibility
data class KxsWMAccountCreated internal constructor(override val uid: String): WMAccountCreated

/** The body of `POST /api/v1/password-resets`. */
interface WMPasswordResetRequest {
    val email: String

    companion object
}

fun WMPasswordResetRequest.Companion.kxs(email: String): KxsWMPasswordResetRequest {
    return KxsWMPasswordResetRequest(email = email)
}

fun WMPasswordResetRequest.toKxs(): KxsWMPasswordResetRequest = when (this) {
    is KxsWMPasswordResetRequest -> this
    else -> WMPasswordResetRequest.kxs(email = email)
}

@Serializable
@SerialName("WMPasswordResetRequest")
@ConsistentCopyVisibility
data class KxsWMPasswordResetRequest internal constructor(override val email: String): WMPasswordResetRequest

/**
 * The token from an emailed link (`oobCode` in the `/auth-action` URL): the body of
 * `POST /api/v1/email-verifications/confirm` and `POST /api/v1/password-resets/verify`.
 */
interface WMAuthActionToken {
    val token: String

    companion object
}

fun WMAuthActionToken.Companion.kxs(token: String): KxsWMAuthActionToken = KxsWMAuthActionToken(token = token)

fun WMAuthActionToken.toKxs(): KxsWMAuthActionToken = when (this) {
    is KxsWMAuthActionToken -> this
    else -> WMAuthActionToken.kxs(token = token)
}

@Serializable
@SerialName("WMAuthActionToken")
@ConsistentCopyVisibility
data class KxsWMAuthActionToken internal constructor(override val token: String): WMAuthActionToken {
    override fun toString(): String = "KxsWMAuthActionToken(token=<redacted>)"
}

/** The body of `POST /api/v1/password-resets/confirm`. */
interface WMPasswordResetConfirm {
    val token: String
    val newPassword: String

    companion object
}

fun WMPasswordResetConfirm.Companion.kxs(token: String, newPassword: String): KxsWMPasswordResetConfirm {
    return KxsWMPasswordResetConfirm(token = token, newPassword = newPassword)
}

fun WMPasswordResetConfirm.toKxs(): KxsWMPasswordResetConfirm = when (this) {
    is KxsWMPasswordResetConfirm -> this
    else -> WMPasswordResetConfirm.kxs(token = token, newPassword = newPassword)
}

@Serializable
@SerialName("WMPasswordResetConfirm")
@ConsistentCopyVisibility
data class KxsWMPasswordResetConfirm internal constructor(
    override val token: String,
    @SerialName("new_password") override val newPassword: String
): WMPasswordResetConfirm {
    override fun toString(): String = "KxsWMPasswordResetConfirm(token=<redacted>)"
}

/** The answer to `POST /api/v1/password-resets/verify`: the account the link resets. */
interface WMPasswordResetTarget {
    val email: String

    companion object
}

fun WMPasswordResetTarget.Companion.kxs(email: String): KxsWMPasswordResetTarget {
    return KxsWMPasswordResetTarget(email = email)
}

fun WMPasswordResetTarget.toKxs(): KxsWMPasswordResetTarget = when (this) {
    is KxsWMPasswordResetTarget -> this
    else -> WMPasswordResetTarget.kxs(email = email)
}

@Serializable
@SerialName("WMPasswordResetTarget")
@ConsistentCopyVisibility
data class KxsWMPasswordResetTarget internal constructor(override val email: String): WMPasswordResetTarget
